import React, { useContext, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Box, Paper, Grid, Typography, Button } from "@mui/material"
import i18n from "i18n"

import AuthContext from "context/auth/authContext"
import InvoiceTotals from "./InvoiceTotals"
import InvoiceConceptRow from "./InvoiceConceptRow"
import DiscountsConfig from "./DiscountsConfig"
import ModalSearchParts from "components/Facturas/modals/modalSearchPart"
import { useTaxes } from "components/ProfileRefinament/Taxes/hooks/useTaxes"
import ProjectContext from "context/projects/projectContext"
import { isGroupedCertificationConcept } from "../utils/invoiceUtils"

const INITIAL_CONCEPT = {
  description: "",
  quantity: 1,
  price: 0,
  taxes: [],
  partidaId: undefined,
  discount: 0,
}

const InvoiceConcepts = ({
  invoiceData,
  setInvoiceData,
  hasValidationErrors,
  conceptsReadOnly = false,
  corrections = [],
}) => {
  const { user } = useContext(AuthContext)
  const organizationId = user["custom:organizationId"]
  const { projects = [] } = useContext(ProjectContext)

  const projectInfo = invoiceData.projectId
    ? projects.find(p => p.projectId === invoiceData.projectId)
    : null

  const [openSearchModal, setOpenSearchModal] = useState(false)
  const [selectedParts, setSelectedParts] = useState([])

  const [showProductDiscount, setShowProductDiscount] = useState(false)
  const [showGlobalDiscount, setShowGlobalDiscount] = useState(
    (invoiceData.discount || 0) > 0,
  )
  const [globalDiscountValue, setGlobalDiscountValue] = useState(
    invoiceData.discount || 0,
  )

  useEffect(() => {
    const hasProductDiscount = (invoiceData.invoiceConcepts || []).some(
      c => (c.discount || 0) > 0,
    )
    if (hasProductDiscount) {
      setShowProductDiscount(true)
    }
  }, [invoiceData.invoiceConcepts])

  useEffect(() => {
    if ((invoiceData.discount || 0) > 0) {
      setShowGlobalDiscount(true)
      setGlobalDiscountValue(invoiceData.discount)
    }
  }, [invoiceData.discount])

  const handleToggleProductDiscount = checked => {
    if (conceptsReadOnly) return
    setShowProductDiscount(checked)
    if (!checked) {
      const updatedConcepts = concepts.map(c => ({ ...c, discount: 0 }))
      setInvoiceData(prev => ({ ...prev, invoiceConcepts: updatedConcepts }))
    }
  }

  const handleToggleGlobalDiscount = checked => {
    setShowGlobalDiscount(checked)
    if (!checked) {
      setGlobalDiscountValue(0)
      setInvoiceData(prev => ({ ...prev, discount: 0 }))
    }
  }

  const handleGlobalDiscountChange = value => {
    setGlobalDiscountValue(value)
    setInvoiceData(prev => ({ ...prev, discount: value }))
  }

  const handleAddFromParts = () => {
    if (conceptsReadOnly) {
      setOpenSearchModal(false)
      setSelectedParts([])
      return
    }
    if (selectedParts.length > 0) {
      setInvoiceData(prev => {
        const currentConcepts = prev.invoiceConcepts || []
        const isFirstConceptEmpty =
          currentConcepts.length === 1 &&
          (!currentConcepts[0].description ||
            currentConcepts[0].description.trim() === "") &&
          (currentConcepts[0].price === 0 || !currentConcepts[0].price)
        const safeCurrentConcepts = currentConcepts.map(c => ({
          ...c,
          ...(c.version !== undefined ? { version: c.version } : {}),
        }))

        const maxPosition =
          safeCurrentConcepts.length > 0
            ? Math.max(...safeCurrentConcepts.map(c => c.position ?? 0))
            : 0

        const newConcepts = selectedParts.map((partida, idx) => ({
          description: partida.name || partida.description || "",
          quantity: partida.quantity || 0,
          maxQuantity: partida.quantity || 0,
          price: partida.clientPrice || 0,
          taxes: [],
          partidaId: partida.partidaId,
          discount: 0,
          position: maxPosition + idx + 1,
        }))
        return {
          ...prev,
          invoiceConcepts: isFirstConceptEmpty
            ? newConcepts
            : [...safeCurrentConcepts, ...newConcepts],
        }
      })
    }
    setSelectedParts([])
    setOpenSearchModal(false)
  }

  const concepts =
    invoiceData.invoiceConcepts && invoiceData.invoiceConcepts.length > 0
      ? [...invoiceData.invoiceConcepts].sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0),
        )
      : conceptsReadOnly
        ? []
        : [INITIAL_CONCEPT]

  useEffect(() => {
    if (
      !conceptsReadOnly &&
      (!invoiceData.invoiceConcepts || invoiceData.invoiceConcepts.length === 0)
    ) {
      setInvoiceData(prev => ({
        ...prev,
        invoiceConcepts: [INITIAL_CONCEPT],
      }))
    }
  }, [conceptsReadOnly, invoiceData.invoiceConcepts, setInvoiceData])

  useEffect(() => {
    if (conceptsReadOnly) {
      setOpenSearchModal(false)
      setSelectedParts([])
    }
  }, [conceptsReadOnly])

  const handleConceptChange = (idx, field, value) => {
    if (conceptsReadOnly) {
      const targetConcept = concepts[idx]
      const canEditDescription =
        field === "description" && isGroupedCertificationConcept(targetConcept)

      if (!canEditDescription) return
    }
    const updatedConcepts = concepts.map((c, i) => {
      if (i === idx) {
        return {
          ...c,
          [field]: value,
          ...(c.version !== undefined ? { version: c.version } : {}),
        }
      }
      return {
        ...c,
        ...(c.version !== undefined ? { version: c.version } : {}),
      }
    })
    setInvoiceData(prev => ({ ...prev, invoiceConcepts: updatedConcepts }))
  }

  const handleTaxesChange = (idx, taxes) => {
    if (invoiceData.issued) return
    const updatedConcepts = concepts.map((c, i) => {
      if (i === idx) {
        return {
          ...c,
          taxes,
          ...(c.version !== undefined ? { version: c.version } : {}),
        }
      }
      return {
        ...c,
        ...(c.version !== undefined ? { version: c.version } : {}),
      }
    })
    setInvoiceData(prev => ({ ...prev, invoiceConcepts: updatedConcepts }))
  }

  const handleAddConcept = () => {
    if (conceptsReadOnly) return
    setInvoiceData(prev => {
      const currentConcepts = prev.invoiceConcepts || []
      const safeCurrentConcepts = currentConcepts.map(c => ({
        ...c,
        ...(c.version !== undefined ? { version: c.version } : {}),
      }))
      const newConcept = {
        ...INITIAL_CONCEPT,
        position: safeCurrentConcepts.length,
      }
      return {
        ...prev,
        invoiceConcepts: [...safeCurrentConcepts, newConcept],
      }
    })
  }

  const handleDeleteConcept = idx => {
    if (conceptsReadOnly) return
    if (concepts.length === 1) return
    setInvoiceData(prev => {
      const filtered = concepts
        .map(c => ({
          ...c,
          ...(c.version !== undefined ? { version: c.version } : {}),
        }))
        .filter((_, i) => i !== idx)
      const reassigned = filtered.map((c, i) => ({ ...c, position: i }))
      return {
        ...prev,
        invoiceConcepts: reassigned,
      }
    })
  }

  const { data: taxes = [] } = useTaxes(organizationId, !!organizationId)

  const taxOptions = taxes.reduce((acc, t) => {
    if (t.active) acc.push(t)
    return acc
  }, [])

  const handleSetTotals = ({ subtotal, tax, retention, total }) => {
    setInvoiceData(prev => {
      if (
        prev.subtotal !== subtotal ||
        prev.tax !== tax ||
        prev.retention !== retention ||
        prev.total !== total
      ) {
        return { ...prev, subtotal, tax, retention, total }
      }
      return prev
    })
  }

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {i18n.t("invoices.concepts")}
        </Typography>
        {projectInfo && !invoiceData.issued && !conceptsReadOnly && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => setOpenSearchModal(true)}
            >
              Buscar partida
            </Button>
          </Box>
        )}

        {concepts.map((concept, idx) => (
          <InvoiceConceptRow
            key={idx}
            concept={concept}
            idx={idx}
            onChange={handleConceptChange}
            onTaxesChange={handleTaxesChange}
            onDelete={handleDeleteConcept}
            disableDelete={concepts.length === 1 || conceptsReadOnly}
            taxOptions={taxOptions}
            hasValidationErrors={hasValidationErrors}
            showProductDiscount={showProductDiscount}
            issued={invoiceData.issued}
            readOnly={conceptsReadOnly}
          />
        ))}
        <Box sx={{ mt: 2 }}>
          {!invoiceData.issued && !conceptsReadOnly && (
            <Button onClick={handleAddConcept}>
              + {i18n.t("invoices.newConcept")}
            </Button>
          )}
        </Box>
        <Grid container justifyContent="space-between" sx={{ mt: 4 }}>
          <DiscountsConfig
            showProductDiscount={showProductDiscount}
            onToggleProductDiscount={handleToggleProductDiscount}
            showGlobalDiscount={showGlobalDiscount}
            onToggleGlobalDiscount={handleToggleGlobalDiscount}
            globalDiscountValue={globalDiscountValue}
            setGlobalDiscountValue={handleGlobalDiscountChange}
            issued={invoiceData.issued}
            disableProductDiscountToggle={conceptsReadOnly}
          />
          <InvoiceTotals
            concepts={concepts}
            setTotals={handleSetTotals}
            globalDiscount={globalDiscountValue}
            corrections={corrections}
          />
        </Grid>
      </Paper>

      {openSearchModal && projectInfo && (
        <ModalSearchParts
          open={openSearchModal}
          setOpen={setOpenSearchModal}
          projectInfo={projectInfo}
          selectedParts={selectedParts}
          setSelectedParts={setSelectedParts}
          handleAddFromParts={handleAddFromParts}
          readOnly={conceptsReadOnly}
        />
      )}
    </>
  )
}

InvoiceConcepts.propTypes = {
  invoiceData: PropTypes.shape({
    invoiceConcepts: PropTypes.array,
    projectId: PropTypes.string,
    discount: PropTypes.number,
    issued: PropTypes.bool,
  }).isRequired,
  setInvoiceData: PropTypes.func.isRequired,
  hasValidationErrors: PropTypes.bool,
  conceptsReadOnly: PropTypes.bool,
  corrections: PropTypes.array,
}

export default InvoiceConcepts
